/**
 * Meta Pixel (Facebook Pixel) Helper & Event Tracking Module
 * Supports dynamic Pixel ID configuration from Admin Settings / Plugin Manager.
 */

let initializedPixelId = typeof window !== "undefined" ? (window.__CURRENT_PIXEL_ID__ || null) : null;
let currentPixelConfig = null;

/**
 * Initializes or re-initializes Meta Pixel with given settings.
 * @param {Object} config - { enabled: boolean, pixel_id: string, test_event_code?: string }
 */
export function initMetaPixel(config) {
  if (typeof window === "undefined") return;

  currentPixelConfig = config;
  const enabled = config?.enabled !== false;
  const pixelId = (config?.pixel_id || "").trim();

  if (!enabled || !pixelId) {
    // Pixel is disabled or not configured
    return;
  }

  // Check if pixel base code is already installed on window
  if (!window.fbq) {
    (function (f, b, e, v, n, t, s) {
      if (f.fbq) return;
      n = f.fbq = function () {
        n.callMethod
          ? n.callMethod.apply(n, arguments)
          : n.queue.push(arguments);
      };
      if (!f._fbq) f._fbq = n;
      n.push = n;
      n.loaded = true;
      n.version = "2.0";
      n.queue = [];
      t = b.createElement(e);
      t.async = true;
      t.src = v;
      s = b.getElementsByTagName(e)[0];
      if (s && s.parentNode) {
        s.parentNode.insertBefore(t, s);
      } else {
        document.head.appendChild(t);
      }
    })(
      window,
      document,
      "script",
      "https://connect.facebook.net/en_US/fbevents.js"
    );
  }

  // Re-initialize if Pixel ID changes or not yet initialized
  if (initializedPixelId !== pixelId) {
    try {
      window.fbq("init", pixelId);
      initializedPixelId = pixelId;
      window.__CURRENT_PIXEL_ID__ = pixelId;
      console.log(`[Meta Pixel] Active Pixel ID set to: ${pixelId}`);

      // Update or create noscript pixel fallback img
      let noscriptImg = document.getElementById("meta-pixel-noscript-img");
      if (!noscriptImg) {
        noscriptImg = document.createElement("img");
        noscriptImg.id = "meta-pixel-noscript-img";
        noscriptImg.height = 1;
        noscriptImg.width = 1;
        noscriptImg.style.display = "none";
        document.body.appendChild(noscriptImg);
      }
      noscriptImg.src = `https://www.facebook.com/tr?id=${encodeURIComponent(pixelId)}&ev=PageView&noscript=1`;
    } catch (err) {
      console.warn("[Meta Pixel] Init error:", err);
    }
  }
}

/**
 * Safe generic event tracker
 */
export function trackPixelEvent(eventName, params = {}, isCustom = false) {
  if (typeof window === "undefined" || !window.fbq) {
    return;
  }
  if (currentPixelConfig && currentPixelConfig.enabled === false) {
    return;
  }
  const effectivePixelId = initializedPixelId || (typeof window !== "undefined" ? window.__CURRENT_PIXEL_ID__ : null);
  if (!effectivePixelId) {
    return;
  }

  try {
    const payload = { ...params };
    if (currentPixelConfig?.test_event_code) {
      payload.test_event_code = currentPixelConfig.test_event_code;
    }

    if (isCustom) {
      window.fbq("trackCustom", eventName, payload);
    } else {
      window.fbq("track", eventName, payload);
    }
  } catch (e) {
    console.warn(`[Meta Pixel] Error tracking ${eventName}:`, e);
  }
}

/**
 * 1. Track PageView
 */
export function trackPageView(pagePath) {
  if (currentPixelConfig && currentPixelConfig.track_pageviews === false) return;
  trackPixelEvent("PageView", pagePath ? { page_path: pagePath } : {});
}

/**
 * 2. Track ViewContent (e.g. Product Details Page)
 */
export function trackViewContent(product) {
  if (!product) return;
  if (currentPixelConfig && currentPixelConfig.track_view_content === false) return;

  const productId = String(product.id || product._id || product.sku || "");
  const price = Number(product.selling_price || product.wholesale_price || product.price || 0);
  const title = product.title || product.name || "Jewellery Item";
  const category = product.category || product.category_name || "Jewellery";

  trackPixelEvent("ViewContent", {
    content_ids: [productId],
    content_name: title,
    content_category: category,
    content_type: "product",
    value: price,
    currency: "INR",
  });
}

/**
 * 3. Track AddToCart
 */
export function trackAddToCart(product, quantity = 1) {
  if (!product) return;
  if (currentPixelConfig && currentPixelConfig.track_add_to_cart === false) return;

  const productId = String(product.id || product._id || product.sku || "");
  const price = Number(product.selling_price || product.wholesale_price || product.price || 0);
  const title = product.title || product.name || "Jewellery Item";

  trackPixelEvent("AddToCart", {
    content_ids: [productId],
    content_name: title,
    content_type: "product",
    value: price * Math.max(1, quantity),
    currency: "INR",
    quantity: Math.max(1, quantity),
  });
}

/**
 * 4. Track InitiateCheckout
 */
export function trackInitiateCheckout(items = [], totalAmount = 0) {
  if (currentPixelConfig && currentPixelConfig.track_initiate_checkout === false) return;

  const contentIds = items.map((i) => String(i.product_id || i.id || i._id || "")).filter(Boolean);
  trackPixelEvent("InitiateCheckout", {
    content_ids: contentIds,
    content_type: "product",
    num_items: items.length,
    value: Number(totalAmount) || 0,
    currency: "INR",
  });
}

/**
 * 5. Track Purchase
 */
export function trackPurchase(order) {
  if (!order) return;
  if (currentPixelConfig && currentPixelConfig.track_purchase === false) return;

  const orderId = String(order.id || order.order_id || order._id || "");
  const value = Number(order.final_amount || order.total_amount || order.amount || 0);
  const items = Array.isArray(order.items) ? order.items : [];
  const contentIds = items.map((i) => String(i.product_id || i.id || "")).filter(Boolean);

  trackPixelEvent("Purchase", {
    content_ids: contentIds,
    content_type: "product",
    num_items: items.length,
    value: value,
    currency: "INR",
    order_id: orderId,
  });
}

/**
 * 6. Track Lead (Custom Request / Design Submission / Inquiries)
 */
export function trackLead(leadData = {}) {
  if (currentPixelConfig && currentPixelConfig.track_leads === false) return;

  trackPixelEvent("Lead", {
    content_name: leadData.name || leadData.category || "Custom Jewellery Inquiry",
    value: Number(leadData.estimated_budget || leadData.budget || 0),
    currency: "INR",
    ...leadData,
  });
}

/**
 * 7. Track Search
 */
export function trackSearch(searchQuery) {
  if (!searchQuery || !searchQuery.trim()) return;
  if (currentPixelConfig && currentPixelConfig.track_search === false) return;

  trackPixelEvent("Search", {
    search_string: searchQuery.trim(),
  });
}

/**
 * 8. Track Contact (WhatsApp click / Phone click / Support)
 */
export function trackContact(channel = "whatsapp") {
  trackPixelEvent("Contact", {
    channel: channel,
  });
}
